import { useState } from 'react';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, App, Button, Card, Empty, Flex, Popconfirm, Space, Table, Tag, Typography, theme } from 'antd';
import { api } from '../api';
import { formatarData } from '../dominio';
import { FormularioDoMaterial } from '../components/FormularioDoMaterial';
import { SITUACAO_COR, SITUACAO_LABEL, situacaoDoMaterial } from './materiais/material';

/**
 * Materiais grátis: o que a casa distribui em grupo e no Instagram.
 *
 * Como nas Notícias e na Revista, **não há botão publicar**: a data é a
 * publicação. A lista mostra, além da situação, a versão do arquivo e os três
 * números que dizem se o material está rendendo - leituras, downloads e cliques
 * vindos de dentro do PDF. O resto do engajamento, com as cópias de cada grupo,
 * mora na gaveta.
 */
export function MateriaisPage() {
  const { token } = theme.useToken();
  const { message: mensagem } = App.useApp();
  const consultas = useQueryClient();
  const [emFormulario, setEmFormulario] = useState<string | null | undefined>();
  const [pagina, setPagina] = useState(1);
  const [tamanhoDaPagina, setTamanhoDaPagina] = useState(20);

  const materiais = useQuery({
    queryKey: ['materiais', 'lista', pagina, tamanhoDaPagina],
    queryFn: () => api.listMaterialsAll(pagina, tamanhoDaPagina),
  });
  const apagar = useMutation({
    mutationFn: (id: string) => api.deleteMaterial(id),
    onSuccess: async () => {
      mensagem.success('Material apagado.');
      if (materiais.data?.items.length === 1 && pagina > 1) setPagina(pagina - 1);
      await consultas.invalidateQueries({ queryKey: ['materiais'] });
    },
    onError: (erro: Error) => mensagem.error(erro.message),
  });

  return (
    <Space orientation="vertical" size={token.marginMD} style={{ width: '100%' }}>
      <Flex justify="space-between" align="flex-start" gap={token.marginSM} wrap>
        <div>
          <Typography.Title level={3} style={{ margin: 0 }}>
            Materiais grátis
          </Typography.Title>
          <Typography.Text type="secondary">
            PDFs para distribuir em grupo e no Instagram. A data de publicação põe o material no ar.
          </Typography.Text>
        </div>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setEmFormulario(null)}>
          Novo material
        </Button>
      </Flex>
      {materiais.error && <Alert type="error" showIcon title={materiais.error.message} />}
      <Card>
        <Table
          rowKey="id"
          loading={materiais.isPending}
          dataSource={materiais.data?.items}
          pagination={{
            /*
             * Sem `hideOnSinglePage`: escondê-la levaria junto o seletor de
             * tamanho, e quem pôs 50 por página numa lista de 25 perderia o
             * controle para voltar a 20 (a regra de ComentarioAssistidoPage).
             */
            current: pagina,
            pageSize: tamanhoDaPagina,
            total: materiais.data?.total ?? 0,
            showSizeChanger: true,
            pageSizeOptions: [10, 20, 50],
            onChange: (proxima, tamanho) => {
              setPagina(tamanho === tamanhoDaPagina ? proxima : 1);
              setTamanhoDaPagina(tamanho);
            },
          }}
          /*
           * Um número, e não `max-content`: com ele a tabela fica do tamanho do
           * título numa linha só, e a coluna de ação sai da tela atrás de uma
           * rolagem que ninguém adivinha. A soma das larguras (880), mais um piso
           * de 210 para o título, que fica com a sobra: cabe inteira na área de
           * 1.100 px que o admin tem numa tela de 1440, e rola com a ação fixa
           * abaixo disso.
           */
          scroll={{ x: 1090 }}
          locale={{ emptyText: <Empty description="Nenhum material cadastrado." /> }}
          columns={[
            {
              title: 'Título',
              dataIndex: 'title',
              render: (titulo: string, linha) => (
                <Typography.Link onClick={() => setEmFormulario(linha.id)}>{titulo}</Typography.Link>
              ),
            },
            {
              title: 'Situação',
              width: 100,
              render: (_, linha) => {
                const situacao = situacaoDoMaterial(linha.publishedAt);
                return <Tag color={SITUACAO_COR[situacao]}>{SITUACAO_LABEL[situacao]}</Tag>;
              },
            },
            {
              title: 'Publicação',
              dataIndex: 'publishedAt',
              width: 170,
              render: (valor: string | null) => formatarData(valor),
            },
            {
              title: 'Versão do arquivo',
              dataIndex: 'fileUpdatedAt',
              width: 170,
              render: (valor: string | null) => (valor ? formatarData(valor) : <Tag>Sem PDF</Tag>),
            },
            { title: 'Leituras', width: 100, align: 'right', render: (_, linha) => linha.totais.views },
            { title: 'Downloads', width: 110, align: 'right', render: (_, linha) => linha.totais.downloads },
            { title: 'Cliques no PDF', width: 150, align: 'right', render: (_, linha) => linha.totais.links },
            {
              title: 'Ações',
              width: 80,
              fixed: 'right',
              render: (_, linha) => (
                <Popconfirm
                  title="Apagar este material?"
                  description="A capa, o PDF e os registros de uso serão apagados. Os links das cópias que circulam passam a levar à lista de materiais."
                  okText="Apagar"
                  cancelText="Cancelar"
                  onConfirm={() => apagar.mutate(linha.id)}
                >
                  <Button
                    type="text"
                    danger
                    icon={<DeleteOutlined />}
                    aria-label={`Apagar ${linha.title}`}
                    loading={apagar.isPending && apagar.variables === linha.id}
                  />
                </Popconfirm>
              ),
            },
          ]}
        />
      </Card>
      {emFormulario !== undefined && (
        <FormularioDoMaterial materialId={emFormulario} onFechar={() => setEmFormulario(undefined)} />
      )}
    </Space>
  );
}
